import { useRequest } from 'ahooks'
import { Button, Form, FormInstance, Modal, Select, Table } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Project, ProjectStage, ProjectType, ReleaseType } from '@/schemas'

import { Stage } from './stages.atom'

interface StageTemplateProps {
  refresh: () => void
  selfId: string
  oldStages: ProjectStage[]
  project: Project
}

interface StageTemplateContentProps {
  form: FormInstance
  project: Project
  oldStages: ProjectStage[]
  onSubmit: (stages: null | { stageId: number; stageGroupId: number; name: string }[]) => void
}

interface StageGroup {
  ename: string
  id: number
  name: string
  stages: {
    id: number
    name: string
    ename: string
    description: string
  }[]
}

const StageTemplateContent: React.FC<StageTemplateContentProps> = ({ form, project, oldStages, onSubmit }) => {
  const { t, i18n } = useTranslation()
  const { data, loading } = useRequest<StageGroup[]>(
    {
      url: '/api/project/getStageGroups',
    },
    {
      cacheKey: 'stage-groups',
      cacheTime: -1,
      staleTime: -1,
      refreshOnWindowFocus: false,
    }
  )

  const [stageGroupId, setStageGroupId] = React.useState<number | null>(null)
  const [stageIds, setStageIds] = React.useState<number[]>([])

  React.useEffect(() => {
    if (oldStages.length) {
      if (oldStages[0].stageGroupId) {
        setStageGroupId(oldStages[0].stageGroupId)
        form.setFieldsValue({
          stageGroupId: oldStages[0].stageGroupId,
        })
      }

      const _stageIds = oldStages.map((s) => s.stageId).filter((s) => !!s)
      setStageIds(_stageIds as number[])
    }
    return () => {
      form.setFieldsValue({
        stageGroupId: null,
      })
    }
  }, [])

  React.useEffect(() => {
    if (project && data && oldStages.length === 0 && project.projectType === ProjectType.RELEASE) {
      const sdkReleaseStage = data.find((value) => value.ename === 'SDK releases')
      const sdkSimpleReleaseStage = data.find((value) => value.ename === 'SDK releases (basic)')
      if (project.releaseType === ReleaseType.MAIN && sdkReleaseStage) {
        form.setFieldsValue({ stageGroupId: sdkReleaseStage.id })
        setStageGroupId(sdkReleaseStage.id)
      }
      if (project.releaseType !== ReleaseType.MAIN && sdkSimpleReleaseStage) {
        form.setFieldsValue({ stageGroupId: sdkSimpleReleaseStage.id })
        setStageGroupId(sdkSimpleReleaseStage.id)
      }
    }
  }, [data, project])

  if (!data) {
    return null
  }

  const onChange = () => {
    const newGroupId = form.getFieldValue('stageGroupId')
    if (stageGroupId !== newGroupId) {
      setStageIds([])
      setStageGroupId(newGroupId)
    }
  }

  const onFinish = () => {
    onSubmit(
      stageGroupId
        ? data
            .find((d) => d.id === stageGroupId)!
            .stages.filter((s) => stageIds.includes(s.id))
            .map((s) => ({
              stageId: s.id,
              stageGroupId,
              name: s.name,
            }))
        : null
    )
  }

  const columns = [
    {
      title: t('stage.name'),
      width: 180,
      render: (d: Stage) => {
        return <span>{i18n.resolvedLanguage === 'zh' ? d.name : d.ename || d.name}</span>
      },
    },
    {
      title: t('stage.description'),
      dataIndex: 'description',
    },
  ]

  return (
    <>
      <Form
        form={form}
        preserve={false}
        onFieldsChange={onChange}
        onFinish={() => onFinish()}
        initialValues={{ stageGroupId }}
      >
        <Form.Item label={t('temp.name')} name='stageGroupId'>
          <Select
            placeholder={t('please select')}
            options={data.map((d) => ({
              label: i18n.resolvedLanguage === 'zh' ? d.name : d.ename || d.name,
              value: d.id,
            }))}
          />
        </Form.Item>
      </Form>
      <Table
        rowKey='id'
        size='small'
        loading={loading}
        pagination={false}
        dataSource={data.find((d) => d.id === stageGroupId)?.stages || []}
        columns={columns}
        rowSelection={{
          selectedRowKeys: stageIds,
          onChange: (keys) => setStageIds(keys as number[]),
        }}
      />
    </>
  )
}

export const StageTemplate: React.FC<StageTemplateProps> = ({ selfId, project, oldStages, refresh }) => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = React.useState(false)
  const [form] = Form.useForm()

  const { run, loading } = useRequest(
    (stages) => ({
      url: '/api/project/createProjectStage',
      method: 'post',
      data: {
        selfId,
        stages,
      },
    }),
    {
      manual: true,
      onSuccess: () => {
        setIsVisible(false)
        refresh()
      },
    }
  )

  const onOk = () => {
    form.submit()
  }

  const onSubmit = (stages: any[] | null) => {
    if (stages && stages.length > 0) {
      if (oldStages.length > 0) {
        Modal.confirm({
          title: t('tooltip'),
          content: t('overwrite'),
          onOk: () => {
            run(stages)
          },
        })
      } else {
        run(stages)
      }
    } else {
      setIsVisible(false)
    }
  }

  return (
    <>
      <Button onClick={() => setIsVisible(true)}>{t('import.temp')}</Button>
      <Modal
        destroyOnClose
        width={800}
        title={t('stage.temp')}
        visible={isVisible}
        onOk={onOk}
        onCancel={() => setIsVisible(false)}
        okButtonProps={{ loading }}
      >
        <StageTemplateContent form={form} project={project} oldStages={oldStages} onSubmit={onSubmit} />
      </Modal>
    </>
  )
}
